/**
 * QL-505 — a declaration-only product type (no assessment configured) for a
 * group without a representative. The type's checkbox becomes the group
 * summary: ticked when every current member ticked, a dash (indeterminate)
 * when some did, empty when none did — with a "4 of 5 confirmed" count next
 * to it and no verdict badge (declarations have none). Expanding lists every
 * member with their own checkbox, each independently actionable.
 */

import { memberLineAttribution } from "@repo/shared";
import type { AnsweringPartyRecord, MemberRepresentative } from "@repo/shared/types";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useId } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./declaration-summary.i18n";
import { MemberLine } from "./member-line";

export type DeclarationMemberState = {
  subjectId: string;
  name: string;
  /** QL-660 — who answers for this member (their own POA/guardian), if anyone. */
  memberRepresentative?: MemberRepresentative | null;
  /** QL-660 — the frozen record of the declaration that counts for this member, if any. */
  declaredBy?: AnsweringPartyRecord | null;
  checked: boolean;
  actionable: boolean;
  onToggle: (checked: boolean) => void;
};

type DeclarationSummaryProps = {
  /** QL-654 — 2+ representatives: the member toggle says "representatives". */
  representativeList: boolean;
  typeLabel: string;
  /** Server aggregate — how many CURRENT members declared experience true. */
  memberSummary?: { passed: number; total: number };
  expanded: boolean;
  onToggleExpand: () => void;
  members: DeclarationMemberState[];
};

export function DeclarationSummary({
  representativeList,
  typeLabel,
  memberSummary,
  expanded,
  onToggleExpand,
  members,
}: DeclarationSummaryProps) {
  const t = useT(T);
  // QL-786 — one block per product type renders the same members, so the
  // checkbox ids need a per-block prefix to stay unique on the page.
  const idPrefix = useId();
  // QL-654 — 2+ representatives answer in the same list as a group's members.
  const expandLabel = representativeList ? t.expandRepresentatives : t.expandMembers;
  const collapseLabel = representativeList ? t.collapseRepresentatives : t.collapseMembers;
  const checkedCount = members.filter((m) => m.checked).length;
  const groupState: boolean | "indeterminate" =
    checkedCount === 0 ? false : checkedCount === members.length ? true : "indeterminate";
  const countText = memberSummary
    ? t.memberCountConfirmed(memberSummary.passed, memberSummary.total)
    : null;

  return (
    <div className="space-y-2">
      {/* A div, not a button: the tri-state summary Checkbox below renders its
          own `<button role="checkbox">` (Radix), and a button cannot legally
          nest inside another button (React DOM-nesting warning). The chevron
          + label toggle is its own button; the checkbox sits beside it as a
          sibling, not a child. */}
      <div className="flex min-h-9 w-full items-center justify-between gap-3">
        <button
          type="button"
          onClick={onToggleExpand}
          aria-expanded={expanded}
          aria-label={`${expanded ? collapseLabel : expandLabel}: ${typeLabel}`}
          className="flex flex-1 items-center gap-2 text-left"
        >
          {expanded ? (
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
          )}
          <Label className="flex-1 font-normal cursor-pointer">{typeLabel}</Label>
        </button>
        {/* Group state only — no member's individual toggle lives here. */}
        <Checkbox checked={groupState} disabled aria-hidden />
        {countText && (
          <span className="shrink-0 whitespace-nowrap text-sm text-muted-foreground">
            {countText}
          </span>
        )}
      </div>
      {expanded && (
        <div className="space-y-1 pl-6">
          {members.map((member) => (
            <MemberLine
              key={member.subjectId}
              subjectId={member.subjectId}
              name={member.name}
              {...memberLineAttribution({
                subjectId: member.subjectId,
                liveRepresentative: member.memberRepresentative,
                // A counted declaration — ticked or not — has a record; "no" is an answer too.
                // An optimistic tick has none yet: the line keeps "answers through" until
                // the saved record names who declared.
                answered: !!member.declaredBy,
                answeredBy: member.declaredBy,
              })}
              language={t.locale}
              controlId={`${idPrefix}-${member.subjectId}`}
              controlDisabled={!member.actionable}
            >
              <Checkbox
                id={`${idPrefix}-${member.subjectId}`}
                checked={member.checked}
                onCheckedChange={(checked) => member.onToggle(checked === true)}
                disabled={!member.actionable}
              />
            </MemberLine>
          ))}
        </div>
      )}
    </div>
  );
}
